import uniqueId from 'lodash/uniqueId';
import React, { memo, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';

import AnimatedArrow from 'components/AnimatedArrow/AnimatedArrow';
import { CarouselSlidesProps } from 'components/Carousel/Carousel';
import ContentMedia from 'components/ContentMedia/ContentMedia';
import Overlay from 'components/Overlay/Overlay';
import CapabilitiesOverlay from 'components/Overlays/CapabilitiesOverlay/CapabilitiesOverlay';
import RichText from 'components/RichText/RichText';
import Text from 'components/Text/Text';
import VideoBlock from 'components/VideoBlock/VideoBlock';
import useOverElement from 'hooks/useOverElement';
import { handleMouseEnter, handleMouseLeave } from 'utils/cursorGlobal';
import { ROUTES } from 'utils/routes';
import { ExpertiseCategoryCMS } from 'utils/sharedStrapiQueries/sharedTypes';
import { breakpointTablet } from 'utils/styles/vars';

import * as S from '../AboutPage.styles';

export interface CapabilitiesSectionProps {
  id: string;
  title: string;
  description: string;
  duration: number;
  carouselSlides: CarouselSlidesProps[];
  filtersLabel: string;
  badge: Array<{
    title: string;
    url?: string;
    id: string;
    expertise_category: { expertiseCategory: ExpertiseCategoryCMS };
    expertise_subcategory_index: number;
  }>;
  upperHeading: string;
}

export interface CapabilitiesSectionMobileCarouselProps {
  slides: CarouselSlidesProps[];
}
const CapabilitiesSectionMobileCarousel: React.FunctionComponent<CapabilitiesSectionMobileCarouselProps> =
  memo(({ slides }) => (
    <S.CapabilitiesSectionDrag>
      <S.CapabilitiesSectionDragInner items={slides.length}>
        {slides.map(slide => (
          <S.CapabilitiesSectionMobileSlide key={uniqueId()}>
            {slide.source === 'azure' || slide.source === 'vimeo' ? (
              <VideoBlock
                videoSource={slide.source}
                videoSourceId={slide.sourceId}
                {...slide}
              />
            ) : (
              <ContentMedia {...slide} />
            )}
            <S.CapabilitiesSectionDragTitle
              size="heading5"
              text={slide.clientName}
            />
            <S.CapabilitiesSectionDragButton
              label={slide.buttonText}
              href={ROUTES.WORK + '/' + slide.caseStudy?.slug}
              transparent
            />
          </S.CapabilitiesSectionMobileSlide>
        ))}
      </S.CapabilitiesSectionDragInner>
    </S.CapabilitiesSectionDrag>
  ));

CapabilitiesSectionMobileCarousel.displayName =
  'CapabilitiesSectionMobileCarousel';

const CapabilitiesSection: React.FunctionComponent<
  CapabilitiesSectionProps
> = ({
  title,
  duration,
  description,
  badge,
  carouselSlides,
  filtersLabel,
  upperHeading,
}) => {
  const [capabilitiesOverlay, setCapabilitiesOverlay] = useState(undefined);
  const { width } = useWindowSize();

  const [ref] = useOverElement();

  const carousel =
    width >= breakpointTablet ? (
      <S.CapabilitiesCarousel
        carouselSlides={carouselSlides}
        duration={duration}
        slidesFullWidth
        showPagination
      />
    ) : (
      <CapabilitiesSectionMobileCarousel slides={carouselSlides} />
    );

  return (
    <S.CapabilitiesSection ref={ref}>
      <S.HeadingWrapper>
        <Text text={upperHeading} size="smallBody" />
        <S.CapabilitiesHeadline>
          <RichText
            as="h2"
            size="heading2"
            richText={title}
            defaultFormatting
          />
        </S.CapabilitiesHeadline>
      </S.HeadingWrapper>

      <S.CapabilitiesDescription>
        <RichText as="p" size="body" richText={description} defaultFormatting />
      </S.CapabilitiesDescription>
      {carousel}
      <S.AboutFiltersHeading>{filtersLabel}</S.AboutFiltersHeading>
      <S.AboutFilters>
        {badge.map(item => (
          <S.Filter
            as={item.url ? 'a' : 'button'}
            target="_blank"
            href={item.url}
            key={uniqueId('capabilities_badge')}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() =>
              !item.url &&
              setCapabilitiesOverlay({
                relatedCaseStudies:
                  item.expertise_category.expertiseCategory.relatedCaseStudies,
                relatedInsights:
                  item.expertise_category.expertiseCategory.relatedInsights,
                ...item.expertise_category.expertiseCategory.listItems[
                  item.expertise_subcategory_index
                ],
              })
            }
          >
            <Text text={item.title} size="smallBody" as="span" />
            <AnimatedArrow />
          </S.Filter>
        ))}
      </S.AboutFilters>
      <Overlay
        isOpen={!!capabilitiesOverlay}
        onExitClick={() => setCapabilitiesOverlay(undefined)}
        delay={0}
      >
        <CapabilitiesOverlay {...capabilitiesOverlay} />
      </Overlay>
    </S.CapabilitiesSection>
  );
};

export default memo(CapabilitiesSection);
